import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createDependency,
  fetchDependencies,
  fetchDependencyAttachments,
  removeDependencyAttachment,
  setDependencyActive,
  transitionDependency,
  updateDependency,
  uploadDependencyAttachment,
  type CreateDependencyInput,
  type UpdateDependencyInput,
} from '../api/dependency.api';

export const DEPENDENCY_KEYS = {
  list: (projectId: string) => ['dependencies', projectId] as const,
  attachments: (projectId: string, dependencyId: string) => ['dependencies', projectId, dependencyId, 'attachments'] as const,
};

export function useDependencies(projectId: string) {
  return useQuery({
    queryKey: DEPENDENCY_KEYS.list(projectId),
    queryFn: () => fetchDependencies(projectId),
    enabled: Boolean(projectId),
  });
}

export function useCreateDependency(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDependencyInput) => createDependency(projectId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DEPENDENCY_KEYS.list(projectId) }),
  });
}

export function useUpdateDependency(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dependencyId, data }: { dependencyId: string; data: UpdateDependencyInput }) =>
      updateDependency(projectId, dependencyId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DEPENDENCY_KEYS.list(projectId) }),
  });
}

export function useTransitionDependency(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dependencyId, toStep }: { dependencyId: string; toStep: string }) =>
      transitionDependency(projectId, dependencyId, toStep),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DEPENDENCY_KEYS.list(projectId) }),
  });
}

// Unbound counterpart to useTransitionDependency(projectId) above — projectId travels in the
// mutate payload instead of being fixed at hook-creation. A cross-project list (Home's
// Dependencies tab) holds rows from many projects, so it can't bind one projectId at the call
// site. Same shape as useTransitionRiskAcrossProjects / useTransitionIssue.
export function useTransitionDependencyAcrossProjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, dependencyId, toStep }: { projectId: string; dependencyId: string; toStep: string }) =>
      transitionDependency(projectId, dependencyId, toStep),
    onSuccess: (_result, { projectId }) => queryClient.invalidateQueries({ queryKey: DEPENDENCY_KEYS.list(projectId) }),
  });
}

export function useSetDependencyActive(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dependencyId, active }: { dependencyId: string; active: boolean }) =>
      setDependencyActive(projectId, dependencyId, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DEPENDENCY_KEYS.list(projectId) }),
  });
}

export function useDependencyAttachments(projectId: string, dependencyId: string) {
  return useQuery({
    queryKey: DEPENDENCY_KEYS.attachments(projectId, dependencyId),
    queryFn: () => fetchDependencyAttachments(projectId, dependencyId),
    enabled: Boolean(dependencyId),
  });
}

export function useUploadDependencyAttachment(projectId: string, dependencyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadDependencyAttachment(projectId, dependencyId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DEPENDENCY_KEYS.attachments(projectId, dependencyId) }),
  });
}

export function useRemoveDependencyAttachment(projectId: string, dependencyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => removeDependencyAttachment(projectId, dependencyId, attachmentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DEPENDENCY_KEYS.attachments(projectId, dependencyId) }),
  });
}
